import React, { useState } from 'react';

export const LOGO_URL = 'https://www.rechargkit.com/img/logo.svg';

/**
 * Geometry of the artwork at https://www.rechargkit.com/img/logo.svg
 * Canvas is 147x50, laid out as: icon glyph top-left at x 6..40 / y 1..23,
 * "RECHARGE" across the bottom from y 24, and "Kit" over on the right.
 * markOnly crops to the glyph box, so re-measure these if the art changes.
 */
const ART = { w: 147, h: 50, icon: { x: 6, y: 1, w: 35, h: 23 } };

/**
 * RechargeKit logo.
 *
 * The artwork is a full lockup (glyph + wordmark) inked in navy and magenta
 * on transparency, so it is drawn on a light chip — against the app's dark
 * chrome the navy alone sits at ~1.6:1 contrast and disappears.
 *
 * height    — height of the lockup in px; width follows the 147:50 ratio
 * markOnly  — crop to the glyph alone (for the collapsed icon rail)
 * size      — edge length of the chip when markOnly
 * chip      — false to drop the light backing (use on already-light surfaces)
 */
export default function BrandLogo({
  height = 28,
  markOnly = false,
  size = 34,
  chip = true,
  style,
  onClick,
  title = 'RechargeKit',
}) {
  const [failed, setFailed] = useState(false);

  const chipStyle = {
    // inline-flex so the chip shrink-wraps the logo in block-level parents
    // instead of stretching the full width of a sidebar panel
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: chip ? '#fff' : 'transparent',
    borderRadius: markOnly ? Math.round(size * 0.26) : Math.round(height * 0.28),
    flexShrink: 0,
    cursor: onClick ? 'pointer' : undefined,
    ...style,
  };

  if (failed) return <BrandFallback {...{ height, markOnly, size, chipStyle, onClick, title }} />;

  if (markOnly) {
    // "RECHARGE" begins on the row directly under the glyph, so the clip window
    // has to be the glyph's exact box — anything larger lets the wordmark in.
    // The window is then centred in the chip by the parent's flex alignment.
    const scale = (size * 0.72) / Math.max(ART.icon.w, ART.icon.h);

    return (
      <div style={{ ...chipStyle, width: size, height: size }} onClick={onClick} title={title}>
        <div style={{
          width: ART.icon.w * scale,
          height: ART.icon.h * scale,
          overflow: 'hidden',
          position: 'relative',
        }}>
          <img
            src={LOGO_URL}
            alt={title}
            onError={() => setFailed(true)}
            style={{
              position: 'absolute',
              width: ART.w * scale,
              height: ART.h * scale,
              left: -ART.icon.x * scale,
              top: -ART.icon.y * scale,
              maxWidth: 'none',
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...chipStyle, height, padding: `0 ${Math.round(height * 0.22)}px` }}
      onClick={onClick} title={title}>
      <img
        src={LOGO_URL}
        alt={title}
        onError={() => setFailed(true)}
        style={{ height: height * 0.72, width: 'auto', display: 'block' }}
      />
    </div>
  );
}

/** Drawn stand-in, used only if the remote logo fails to load. */
function BrandFallback({ height, markOnly, size, chipStyle, onClick, title }) {
  const box = markOnly ? size : height;
  return (
    <div style={{
      ...chipStyle,
      background: 'linear-gradient(135deg,#5865f2,#7c3aed)',
      height: box,
      gap: 8,
      padding: markOnly ? 0 : `0 ${Math.round(height * 0.28)}px`,
      width: markOnly ? size : undefined,
    }} onClick={onClick} title={title}>
      <BrandMark size={Math.round(box * 0.56)} />
      {!markOnly && (
        <span style={{ fontSize: Math.round(height * 0.5), fontWeight: 800, letterSpacing: '-0.02em', color: '#fff', whiteSpace: 'nowrap' }}>
          RechargeKit
        </span>
      )}
    </div>
  );
}

/** Charging-cell glyph backing the fallback. */
export function BrandMark({ size = 18, color = '#fff' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="2.5" y="5.5" width="16" height="13" rx="3.5" stroke={color} strokeWidth="1.8" />
      <path d="M21 10v4" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      <path d="M11.6 8.2 8.4 12.6h2.6l-1 3.2 3.4-4.6h-2.7l.9-3z" fill={color} />
    </svg>
  );
}
